import { useState, useCallback } from 'react'
import { Upload, FileText, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { DocumentPicker, type PickedDocument } from '@/components/document-picker'
import { cn } from '@/lib/utils'

export type { PickedDocument } from '@/components/document-picker'

interface AttachmentSourcePickerProps {
  projectId: string
  /** Called when user picks "upload from computer" */
  onUploadFromComputer: () => void
  /** Called when user selects existing documents */
  onDocumentsSelected: (documents: PickedDocument[]) => void
  /** Already-attached document IDs (shown as locked in picker) */
  attachedDocIds?: string[]
  children: React.ReactNode
}

type View = 'menu' | 'picker'

export function AttachmentSourcePicker({
  projectId,
  onUploadFromComputer,
  onDocumentsSelected,
  attachedDocIds,
  children,
}: AttachmentSourcePickerProps) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>('menu')

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (next) setView('menu')
  }, [])

  const handleUpload = useCallback(() => {
    setOpen(false)
    onUploadFromComputer()
  }, [onUploadFromComputer])

  const handleDocumentsSelected = useCallback(
    (docs: PickedDocument[]) => {
      setOpen(false)
      onDocumentsSelected(docs)
    },
    [onDocumentsSelected]
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className={cn('p-0', view === 'menu' ? 'w-64' : 'w-96')}
      >
        {view === 'menu' ? (
          <div className="p-1">
            <button
              type="button"
              onClick={handleUpload}
              className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors"
            >
              <Upload className="h-4 w-4 text-muted-foreground" />
              {t('attachmentPicker.uploadFromComputer')}
            </button>
            <button
              type="button"
              onClick={() => setView('picker')}
              className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors"
            >
              <FileText className="h-4 w-4 text-muted-foreground" />
              {t('attachmentPicker.chooseDocuments')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col min-h-[300px]">
            {/* Header with back button */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
              <button
                type="button"
                onClick={() => setView('menu')}
                aria-label={t('attachmentPicker.back')}
                className="p-0.5 rounded hover:bg-accent text-muted-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium">{t('attachmentPicker.chooseDocuments')}</span>
            </div>

            <DocumentPicker
              projectId={projectId}
              selectedIds={attachedDocIds}
              onSelect={handleDocumentsSelected}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
